import { useState, useCallback, useRef } from 'react';
import { Button } from '../ui';
import type { ParsePreview } from '../../types';
import { Card } from '../ui';
import { TabButton, CollapsibleSection, MessagePreview } from './helpers';
import { formatTimestamp } from './constants';

type InputTab = 'paste' | 'upload' | 'system';

export function InputPanel({
  onAnalyze,
  onCancel,
  isRunning,
  preview,
}: {
  onAnalyze: (text: string, file: File | null, inboundOnly: boolean) => void;
  onCancel: () => void;
  isRunning: boolean;
  preview: ParsePreview | null;
}): JSX.Element {
  const [activeTab, setActiveTab] = useState<InputTab>('paste');
  const [pasteText, setPasteText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [inboundOnly, setInboundOnly] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasInput = activeTab === 'paste' ? pasteText.trim().length > 0 : file !== null;

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setActiveTab('upload');
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  }, []);

  const handleAnalyze = () => {
    if (activeTab === 'paste') {
      onAnalyze(pasteText, null, inboundOnly);
    } else if (activeTab === 'upload' && file) {
      onAnalyze('', file, inboundOnly);
    }
  };

  return (
    <div className="w-[350px] flex-shrink-0 bg-surface-elevated border-r border-border-subtle overflow-y-auto flex flex-col">
      {/* Header */}
      <div className="px-4 py-4 border-b border-border-subtle">
        <h1 className="font-heading text-lg font-semibold text-text-primary">
          Анализатор переписок
        </h1>
        <p className="text-text-secondary text-sm mt-1">
          Загрузите переписку для анализа
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border-subtle">
        <TabButton active={activeTab === 'paste'} onClick={() => setActiveTab('paste')}>
          Вставить текст
        </TabButton>
        <TabButton active={activeTab === 'upload'} onClick={() => setActiveTab('upload')}>
          Загрузить файл
        </TabButton>
        <TabButton
          active={activeTab === 'system'}
          disabled
          onClick={() => {}}
          tooltip="Скоро"
        >
          Из системы
        </TabButton>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 space-y-4">
        {/* Input area */}
        {activeTab === 'paste' && (
          <div>
            <label className="block mb-1.5 text-sm font-medium text-text-secondary">
              Текст переписки
            </label>
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="Вставьте текст переписки (WhatsApp export или любой текст)..."
              className={[
                'w-full h-[200px] px-3 py-2',
                'bg-surface-subtle border border-border-subtle rounded-md',
                'text-text-primary placeholder:text-text-secondary',
                'resize-none',
                'focus:outline-none focus:border-accent-brand focus:ring-1 focus:ring-accent-action/50',
              ].join(' ')}
              disabled={isRunning}
            />
          </div>
        )}

        {activeTab === 'upload' && (
          <div>
            <label className="block mb-1.5 text-sm font-medium text-text-secondary">
              Файл переписки
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={[
                'h-[200px] flex flex-col items-center justify-center',
                'border-2 border-dashed rounded-lg cursor-pointer transition-colors',
                isDragging
                  ? 'border-accent-brand bg-accent-brand/10'
                  : 'border-border-subtle bg-surface-subtle hover:border-border-strong',
                isRunning ? 'opacity-50 pointer-events-none' : '',
              ].join(' ')}
            >
              {file ? (
                <>
                  <svg
                    className="w-10 h-10 text-accent-success mb-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-text-primary font-medium">{file.name}</p>
                  <p className="text-text-secondary text-sm">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    className="mt-2 text-sm text-accent-danger hover:text-red-300"
                  >
                    Удалить
                  </button>
                </>
              ) : (
                <>
                  <svg
                    className="w-10 h-10 text-text-secondary mb-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                    />
                  </svg>
                  <p className="text-text-primary">Перетащите файл сюда</p>
                  <p className="text-text-secondary text-sm">или нажмите для выбора</p>
                  <p className="text-text-secondary text-xs mt-2">.txt, .log, .md</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.log,.md,text/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}

        {activeTab === 'system' && (
          <div className="h-[200px] flex items-center justify-center bg-surface-subtle rounded-lg border border-border-subtle">
            <p className="text-text-secondary">Функция будет доступна позже</p>
          </div>
        )}

        {/* Inbound only toggle */}
        <div className="flex items-center justify-between">
          <label className="text-sm text-text-secondary">
            Только входящие сообщения
          </label>
          <button
            type="button"
            onClick={() => setInboundOnly(!inboundOnly)}
            disabled={isRunning}
            className={[
              'relative w-11 h-6 rounded-full transition-colors',
              inboundOnly ? 'bg-accent-action' : 'bg-[#2a2a2d]',
              isRunning ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ].join(' ')}
          >
            <span
              className={[
                'absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform',
                inboundOnly ? 'translate-x-5' : '',
              ].join(' ')}
            />
          </button>
        </div>

        {/* Preview */}
        {preview && (
          <Card className="space-y-3">
            <div className="flex items-center gap-2">
              <span
                className={[
                  'px-2 py-1 text-xs rounded-full',
                  preview.format === 'whatsapp'
                    ? 'bg-accent-success/20 text-accent-success'
                    : 'bg-accent-warning/20 text-accent-warning',
                ].join(' ')}
              >
                {preview.format === 'whatsapp' ? 'WhatsApp' : 'Текст'}
                {preview.format === 'whatsapp' ? ' ✓' : ' ⚠'}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div>
                <div className="text-text-primary font-medium">{preview.messagesCount}</div>
                <div className="text-text-secondary text-xs">сообщений</div>
              </div>
              <div>
                <div className="text-text-primary font-medium">{preview.participantsCount}</div>
                <div className="text-text-secondary text-xs">участников</div>
              </div>
              <div>
                <div className="text-text-primary font-medium">
                  {preview.periodStart && preview.periodEnd
                    ? `${formatTimestamp(preview.periodStart).split(',')[0]} - ${formatTimestamp(preview.periodEnd).split(',')[0]}`
                    : '-'}
                </div>
                <div className="text-text-secondary text-xs">период</div>
              </div>
            </div>
            {preview.firstMessages.length > 0 && (
              <CollapsibleSection title="Первые сообщения" defaultOpen={false}>
                {preview.firstMessages.map((msg) => (
                  <MessagePreview key={msg.id} message={msg} />
                ))}
              </CollapsibleSection>
            )}
            {preview.lastMessages.length > 0 && (
              <CollapsibleSection title="Последние сообщения" defaultOpen={false}>
                {preview.lastMessages.map((msg) => (
                  <MessagePreview key={msg.id} message={msg} />
                ))}
              </CollapsibleSection>
            )}
          </Card>
        )}

        {/* Actions */}
        <div className="space-y-2 pt-2">
          {isRunning ? (
            <Button
              variant="secondary"
              size="md"
              onClick={onCancel}
              className="w-full"
            >
              <span className="flex items-center gap-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Отменить
              </span>
            </Button>
          ) : (
            <Button
              variant="primary"
              size="md"
              onClick={handleAnalyze}
              disabled={!hasInput}
              className="w-full"
            >
              <span className="flex items-center gap-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  />
                </svg>
                Анализировать
              </span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
